import { useMemo, useState, useCallback } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { LeaderboardTable } from './LeaderboardTable';
import { ChartContainer } from '../shared/ChartContainer';
import { EmptyState } from '../shared/EmptyState';
import { ErrorState } from '../shared/ErrorState';
import { LeaderboardSkeleton } from '../shared/AnalyticsSkeletons';
import { ExportButton } from '../shared/ExportButton';
import { PIIExportDialog } from '../shared/PIIExportDialog';
import { useCSVExport } from '../../../hooks/useCSVExport';
import { useToast } from '@/shared/components/Toast';
import type { SPProviderRow, SortOrder } from '../../../types/analytics.types';

type SPClientLeaderboardProps = {
  providers: SPProviderRow[];
  totalCount: number;
  search: string;
  setSearch: (value: string) => void;
  sortBy: string;
  sortOrder: SortOrder;
  handleSort: (column: string) => void;
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  onRetry?: () => void;
  exportParams?: Record<string, string | number | boolean | undefined>;
};

const RankBadge = ({ rank }: { rank: number }) => (
  <div className="w-8 h-8 rounded-full bg-[#3d6560] text-white flex items-center justify-center text-sm font-medium">
    {rank}
  </div>
);

const ClientList = ({ names }: { names: string[] }) => {
  if (names.length === 0) return <span className="text-gray-400">—</span>;
  const display = names.slice(0, 3);
  const remainder = names.length - 3;
  return (
    <div className="text-sm">
      {display.join(', ')}
      {remainder > 0 && (
        <span className="text-gray-400"> +{remainder} more</span>
      )}
    </div>
  );
};

export const SPClientLeaderboard = ({
  providers,
  totalCount,
  search,
  setSearch,
  sortBy,
  sortOrder,
  handleSort,
  isLoading,
  isError,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  onRetry,
  exportParams,
}: SPClientLeaderboardProps) => {
  const [showPIIDialog, setShowPIIDialog] = useState(false);
  const { showToast } = useToast();

  const handleExportError = useCallback(
    (message: string) => {
      showToast(`Export failed: ${message}. Please try again.`, 'error', 5000);
    },
    [showToast],
  );

  const { exportCSV, isExporting } = useCSVExport({ onError: handleExportError });

  const handleExportClick = useCallback(() => {
    setShowPIIDialog(true);
  }, []);

  const handleExportConfirm = useCallback(async () => {
    await exportCSV('sp-clients', exportParams);
    setShowPIIDialog(false);
  }, [exportCSV, exportParams]);

  const columns = useMemo<ColumnDef<SPProviderRow, unknown>[]>(
    () => [
      {
        id: 'rank',
        header: '#',
        enableSorting: false,
        cell: ({ row }) => <RankBadge rank={row.index + 1} />,
        size: 60,
      },
      {
        id: 'spName',
        accessorKey: 'spName',
        header: 'SP Name',
        cell: ({ getValue }) => (
          <span className="font-medium text-gray-900">
            {getValue() as string}
          </span>
        ),
      },
      {
        id: 'companyName',
        accessorKey: 'companyName',
        header: 'Company',
        cell: ({ getValue }) => (
          <span className="text-gray-700">{getValue() as string}</span>
        ),
      },
      {
        id: 'serviceCategory',
        accessorKey: 'serviceCategory',
        header: 'Category',
        enableSorting: false,
        cell: ({ getValue }) => (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#e8f4f2] text-[#3d6560]">
            {getValue() as string}
          </span>
        ),
      },
      {
        id: 'totalClients',
        accessorKey: 'totalClients',
        header: 'Total Clients',
        cell: ({ getValue }) => (
          <span className="font-semibold text-[#3d6560]">
            {getValue() as number}
          </span>
        ),
      },
      {
        id: 'totalReferrals',
        accessorKey: 'totalReferrals',
        header: 'Total Referrals',
        cell: ({ getValue }) => (getValue() as number).toString(),
      },
      {
        id: 'clientNames',
        header: 'Client Names',
        enableSorting: false,
        cell: ({ row }) => (
          <ClientList
            names={row.original.clients.map((c) => c.clientName)}
          />
        ),
      },
      {
        id: 'clientEmails',
        header: 'Client Emails',
        enableSorting: false,
        cell: ({ row }) => (
          <ClientList
            names={row.original.clients.map((c) => c.clientEmail)}
          />
        ),
      },
    ],
    [],
  );

  if (isLoading) return <LeaderboardSkeleton />;

  if (isError) {
    return (
      <ChartContainer
        title="Service Provider Client Leaderboard"
        subtitle="SP client activity and referrals"
      >
        <ErrorState
          message="Failed to load SP client data. Please try again."
          onRetry={onRetry}
        />
      </ChartContainer>
    );
  }

  if (providers.length === 0) {
    return (
      <ChartContainer
        title="Service Provider Client Leaderboard"
        subtitle="SP client activity and referrals"
      >
        <EmptyState message="No service provider data found for the selected filters." />
      </ChartContainer>
    );
  }

  return (
    <>
      <ChartContainer
        title="Service Provider Client Leaderboard"
        subtitle="SP client activity and referrals"
        action={
          <ExportButton
            onClick={handleExportClick}
            isLoading={isExporting}
            label="Export CSV"
          />
        }
      >
        <LeaderboardTable
          data={providers}
          columns={columns}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by provider or company..."
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          totalCount={totalCount}
          countLabel="providers"
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={fetchNextPage}
        />
      </ChartContainer>
      <PIIExportDialog
        open={showPIIDialog}
        onOpenChange={setShowPIIDialog}
        onConfirm={handleExportConfirm}
        exportType="SP Clients"
        isLoading={isExporting}
      />
    </>
  );
};
